import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './addParentToChildDialog.module.scss'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { ATTACH_CHILD_TO_PARENT, GET_PARENT_LIST } from '@/modules/API/parent/parentHandler'
import { Parent } from '@/types/Parent'
import { ChildParent } from '@/types/Child'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import Loading from '@/components/Loading/Loading'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface AddParentToChildDialogProps {
    open: boolean
    handleClose: () => void
    childId: number
    childName: string
    daycareId: number
    parents: ChildParent[]
    handleRefetchChild: () => void
}

const AddParentToChildDialog: FunctionComponent<AddParentToChildDialogProps> = ({
    open = false,
    handleClose,
    childId,
    daycareId,
    childName,
    parents,
    handleRefetchChild
}: AddParentToChildDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [parentList, setParentList] = useState<Parent[]>([])
    const [selectedParentId, setSelectedParentId] = useState<string>('0')

    const {
        loading: loadingGetParents,
        data: dataGetParents,
        error: errorGetParents
    } = useQuery(GET_PARENT_LIST, {
        variables: {
            daycare_id: daycareId
        }
    })

    useEffect(() => {
        if (dataGetParents) {
            const list: Parent[] = dataGetParents.getParentList.filter(
                (parent: Parent): boolean =>
                    !parents.find((childParent: ChildParent): boolean => childParent.parentId === parent.id)
            )

            setParentList(list)
        }
    }, [dataGetParents])

    useEffect(() => {
        if (errorGetParents) {
            handleClose()
        }
    }, [errorGetParents])

    const [daycareAttachChild] = useMutation(ATTACH_CHILD_TO_PARENT, {
        variables: {
            parentId: Number(selectedParentId),
            daycareId: daycareId,
            childId: childId
        },
        onCompleted: () => {
            handleRefetchChild()

            showNotificationSuccess(t('adultAdded'))
            handleClose()
        },
        onError: () => {
            showNotificationError(t('adultAddedError'))
            handleClose()
        }
    })

    const selectOptions: SelectOptionsDeprecated = [
        {
            options: parentList
                .filter((parent: Parent): boolean => parent.id !== 0 && parent.email !== '' && parent.lastname !== '')
                .sort((parentA: Parent, parentB: Parent): number => parentA.lastname.localeCompare(parentB.lastname))
                .map((parent: Parent): SelectOptionDeprecated => {
                    return {
                        value: parent.id.toString(),
                        name: parent.lastname + ' ' + parent.firstname
                    }
                })
        }
    ]

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('addAdult')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>
                    {t('whichAdultYouWantToAdd')}
                    <span className={styles.TextFamily}>{childName}</span>
                    {' ?'}
                </p>
                <p className={styles.Text}>{t('noRollBack')}</p>
                {loadingGetParents && <Loading overall={false} />}
                {errorGetParents && <ErrorMessage error={t('noAdults')} />}
                {dataGetParents && (
                    <>
                        {parentList.length > 0 ? (
                            <div className={styles.InputContainer}>
                                <SelectUnique
                                    selectOptions={selectOptions}
                                    selectValue={selectedParentId}
                                    setSelectValue={setSelectedParentId}
                                    className={styles.Select}
                                    label={t('adults')}
                                />
                            </div>
                        ) : (
                            <ErrorMessage className={styles.Text} error={t('noAdults')} />
                        )}
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'add'}
                                onClick={daycareAttachChild}
                                className={styles.Button}
                                disabled={selectedParentId === '0'}
                            />
                            <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                        </div>
                    </>
                )}
            </DialogBody>
        </Dialog>
    )
}

export default AddParentToChildDialog
